import React, { useState, useEffect } from 'react';
import { FaSearch, FaUserMinus, FaTimes, FaExclamationTriangle, FaFileAlt, FaUserPlus, FaUser, FaDownload, FaEdit, FaExchangeAlt, FaCheckSquare, FaSquare } from 'react-icons/fa';
import { Student } from '../types';
import { API_BASE_URL } from '../utils/apiConfig';
import { generateWithdrawalGradeReport, getValidLevelsCount, getValidLevelsAchieved, calculateDimensionStatistics } from '../utils/gradeCalculationForWithdrawal';
import { dimensionsTemplate } from '../App';
import { Dimension } from '../types';

// Función para obtener nombre de dimensión por ID
const getDimensionNameById = (dimensionId: number): string => {
  const DIMENSION_NAMES: { [key: number]: string } = {
    1: "Autoconciencia y Voluntad",
    2: "Habilidades de pensamiento",
    3: "Habilidades comunicacionales en el lenguaje español",
    4: "Corporalidad y movimiento",
    5: "Lenguaje Artístico",
    6: "Motivación para el aprendizaje",
    7: "Encuentro con la Naturaleza y Sociedad",
    8: "Habilidades sociales",
    9: "Pensamiento matemático",
    10: "Acercamiento a la lengua inglesa",
    11: "Amor a Dios y sentido Cristiano"
  };
  
  return DIMENSION_NAMES[dimensionId] || `Dimensión ${dimensionId}`;
};

interface ActiveStudent {
  id: string;
  name: string;
  course: string;
  birthDate: string;
  gender?: string;
  skillLevels?: Array<{
    dimensionId: number;
    skillId: number;
    level: number;
  }>;
  qualitativeDescription?: string;
}

interface WithdrawnStudent {
  id: string;
  originalId?: string;
  name: string;
  course: string;
  birthDate?: string;
  withdrawalDate: string;
  skillLevels?: Array<{
    dimensionId: number;
    skillId: number;
    level: number | null;
  }>;
  finalProgressSummary: {
    totalSkills: number;
    averageLevel: number;
    completedSkills: number;
    completionPercentage: number;
  };
  finalGrades?: {
    overall: number;
    byDimension: { [key: string]: { grade: number; skillCount: number } };
    bySubject?: { [subject: string]: { grade: number; skillCount: number; details: any[] } };
    dimensionSummary?: { 
      [key: string]: { 
        dimensionName: string;
        areaName: string;
        expectedPoints: number;
        achievedPoints: number;
        percentage: number;
        grade: number;
        skillCount: number;
        totalSkills: number;
        achievedValidLevels?: number;
        totalValidLevels?: number;
        completeness?: number;
        targetAge?: number;
      } 
    };
  };
  reason?: string;
  additionalNotes?: string;
  processedBy?: string;
  detailedReport?: {
    strengths: string[];
    areasForImprovement: string[];
    recommendations: string[];
  };
}

interface RetiroProps {
  students: Student[];
  onSectionChange: (section: string) => void;
}

const Retiro: React.FC<RetiroProps> = ({
  students,
  onSectionChange
}) => {

  const [activeStudents, setActiveStudents] = useState<ActiveStudent[]>([]);
  const [withdrawnStudents, setWithdrawnStudents] = useState<WithdrawnStudent[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<ActiveStudent | null>(null);
  const [withdrawalReason, setWithdrawalReason] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [activeTab, setActiveTab] = useState<'active' | 'withdrawn'>('active');
  const [selectedWithdrawnStudent, setSelectedWithdrawnStudent] = useState<WithdrawnStudent | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState<WithdrawnStudent | null>(null);
  const [newStudentData, setNewStudentData] = useState({
    firstName: '',
    lastName: '',
    secondLastName: '',
    id: '',
    course: '',
    birthDate: '',
    gender: 'M',
    qualitativeDescription: ''
  });
  const [availableCourses, setAvailableCourses] = useState<string[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  
  // New state for edit and multi-select functionality
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editStudentData, setEditStudentData] = useState({
    id: '',
    originalId: '',
    firstName: '',
    lastName: '',
    secondLastName: '',
    newId: '',
    course: '',
    birthDate: '',
    gender: 'M'
  });
  const [showBulkCourseModal, setShowBulkCourseModal] = useState(false);
  const [bulkNewCourse, setBulkNewCourse] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  
  const courses = Array.from(new Set(activeStudents.map(student => student.course))).sort();

  // Get token from localStorage
  const getToken = () => {
    return localStorage.getItem('token');
  };

  // Load active students
  const loadActiveStudents = async () => {
    try {
      setLoading(true);
      const token = getToken();
      if (!token) {
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/students/active?search=${encodeURIComponent(searchTerm)}&course=${encodeURIComponent(selectedCourse)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        setActiveStudents([]);
        return;
      }

      const data = await response.json();
      if (data.success && Array.isArray(data.students)) {
        setActiveStudents(data.students);
      } else {
        setActiveStudents([]);
      }
    } catch (error) {
      setActiveStudents([]);
    } finally {
      if (initialLoading) {
        setTimeout(() => setLoading(false), 500);
      } else {
        setLoading(false);
      }
    }
  };

  // Load withdrawn students
  const loadWithdrawnStudents = async () => {
    try {
      setLoading(true);
      const token = getToken();
      if (!token) {
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/students/withdrawn`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        setWithdrawnStudents([]);
        return;
      }

      const data = await response.json();
      
      if (data.success && Array.isArray(data.students)) {
        setWithdrawnStudents(data.students);
      } else {
        setWithdrawnStudents([]);
      }
    } catch (error) {
      setWithdrawnStudents([]);
    } finally {
      if (initialLoading) {
        setTimeout(() => setLoading(false), 500);
      } else {
        setLoading(false);
      }
    }
  };

  // Add new student
  const addStudent = async () => {
    if (!newStudentData.firstName || !newStudentData.lastName || !newStudentData.id || 
        !newStudentData.course || !newStudentData.birthDate) {
      alert('Por favor completa todos los campos requeridos (*): Nombres, Apellido Paterno, ID/RUT, Curso y Fecha de Nacimiento.');
      return;
    }

    try {
      setLoading(true);
      const token = getToken();
      if (!token) {
        alert('❌ No se encontró token de autenticación');
        return;
      }

      const fullName = `${newStudentData.lastName}${newStudentData.secondLastName ? ' ' + newStudentData.secondLastName : ''} ${newStudentData.firstName}`.trim();
      
      const studentData = {
        name: fullName,
        firstName: newStudentData.firstName,
        lastName: newStudentData.lastName, 
        secondLastName: newStudentData.secondLastName || '',
        id: newStudentData.id,
        course: newStudentData.course,
        birthDate: newStudentData.birthDate,
        gender: newStudentData.gender,
        qualitativeDescription: newStudentData.qualitativeDescription || ''
      };

      const response = await fetch(`${API_BASE_URL}/api/students/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(studentData)
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        alert(`✅ Estudiante ${fullName} agregado exitosamente al curso ${newStudentData.course}`);
        closeAddStudentModal();
        loadActiveStudents();
      } else {
        alert(`❌ Error al agregar estudiante: ${data.message || 'Error desconocido'}`);
      }
    } catch (error) {
      alert(`❌ Error de conexión al agregar estudiante`);
    } finally {
      setLoading(false);
    }
  };

  // Withdraw student
  const withdrawStudent = async () => {
    if (!selectedStudent) return;

    try {
      setLoading(true);
      const token = getToken();
      if (!token) {
        alert('❌ No se pudo obtener el token de autenticación');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/students/withdraw`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          studentId: selectedStudent.id,
          reason: withdrawalReason,
          additionalNotes: additionalNotes
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        alert(`✅ Estudiante ${selectedStudent.name} retirado exitosamente`);
        setShowWithdrawModal(false);
        setSelectedStudent(null);
        setWithdrawalReason('');
        setAdditionalNotes('');
        
        await loadActiveStudents();
        await loadWithdrawnStudents();
      } else {
        alert(`❌ Error: ${data.message}`);
      }
    } catch (error) {
      alert(`❌ Error al retirar estudiante`);
    } finally {
      setLoading(false);
    }
  };

  // Calculate student progress using corrected formula
  const calculateProgress = (student: ActiveStudent) => {
    if (!student || !student.skillLevels || student.skillLevels.length === 0) {
      return { completed: 0, total: 0, percentage: 0 };
    }
    
    try {
      const total = student.skillLevels.length;
      
      // ✅ CORRECCIÓN: Usar nueva fórmula con año base para calcular progreso real
      const totalScore = student.skillLevels.reduce((sum, skill) => {
        if (skill && skill.level !== undefined && skill.level !== null) {
          // Aplicar fórmula corregida: (level + 1) * 20 + 20
          return sum + (skill.level + 1) * 20 + 20;
        }
        return sum;
      }, 0);
      
      const averageScore = total > 0 ? totalScore / total : 0;
      const percentage = Math.round(averageScore);
      
      // Para mostrar "completadas", contar skills con nivel > 0
      const completed = student.skillLevels.filter(skill => 
        skill && skill.level !== undefined && skill.level !== null && skill.level > 0
      ).length;
      
      return { completed, total, percentage };
    } catch (error) {
      return { completed: 0, total: 0, percentage: 0 };
    }
  };

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      await loadActiveStudents();
      await loadWithdrawnStudents();

      setTimeout(() => {
        setInitialLoading(false);
      }, 3000);
    };

    loadData();
  }, []);

  // Debounced search effect
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (activeTab === 'active') {
        loadActiveStudents();
      } else {
        loadWithdrawnStudents();
      }
    }, searchTerm ? 300 : 0);

    return () => clearTimeout(delayedSearch);
  }, [activeTab, searchTerm, selectedCourse]);

  const openWithdrawModal = (student: ActiveStudent) => {
    setSelectedStudent(student);
    setShowWithdrawModal(true);
    setWithdrawalReason('');
    setAdditionalNotes('');
  };

  const closeWithdrawModal = () => {
    setShowWithdrawModal(false);
    setSelectedStudent(null);
    setWithdrawalReason('');
    setAdditionalNotes('');
  };

  const openReportModal = (student: WithdrawnStudent) => {
    console.log('📊 Abriendo informe de retiro para:', student.name);
    console.log('📋 finalGrades structure:', student.finalGrades);
    console.log('📚 bySubject data:', student.finalGrades?.bySubject);
    console.log('📋 Complete student data:', JSON.stringify(student, null, 2));
    setSelectedWithdrawnStudent(student);
    setShowReportModal(true);
  };

  const closeReportModal = () => {
    setShowReportModal(false);
    setSelectedWithdrawnStudent(null);
  };

  // Update existing withdrawn students with subject grades
  const updateExistingWithdrawnStudents = async () => {
    try {
      setLoading(true);
      const token = getToken();
      if (!token) {
        alert('❌ No se pudo obtener el token de autenticación');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/students/update-withdrawn-grades`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        alert(`✅ ${result.message}`);
        // Recargar la lista de estudiantes retirados
        await loadWithdrawnStudents();
      } else {
        alert(`❌ Error: ${result.message}`);
      }
    } catch (error) {
      console.error('Error actualizando estudiantes retirados:', error);
      alert(`❌ Error al actualizar estudiantes retirados: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    } finally {
      setLoading(false);
    }
  };

  // Export withdrawal report as PDF
  const exportWithdrawalReport = async () => {
    if (!selectedWithdrawnStudent) return;

    try {
      setLoading(true);
      const token = getToken();
      if (!token) {
        alert('❌ No se pudo obtener el token de autenticación');
        return;
      }

      console.log('📄 Intentando descargar informe para estudiante:', selectedWithdrawnStudent.id);

      const response = await fetch(`${API_BASE_URL}/api/students/withdrawal-report/${selectedWithdrawnStudent.id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📄 Respuesta del servidor:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('📄 Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const contentType = response.headers.get('content-type');
      console.log('📄 Content-Type:', contentType);

      if (contentType && contentType.includes('text/html')) {
        // Es HTML, descargarlo como archivo HTML
        const htmlContent = await response.text();
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `Informe_Retiro_${selectedWithdrawnStudent.name}_${new Date().toISOString().split('T')[0]}.html`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        // Otro tipo de contenido
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `Informe_Retiro_${selectedWithdrawnStudent.name}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }

      alert('✅ Informe descargado exitosamente');
    } catch (error) {
      console.error('Error descargando informe:', error);
      alert(`❌ Error al descargar el informe: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    } finally {
      setLoading(false);
    }
  };

  const openAddStudentModal = () => {
    setShowAddStudentModal(true);
    loadAvailableCourses();
  };

  const closeAddStudentModal = () => {
    setShowAddStudentModal(false);
    setNewStudentData({
      firstName: '',
      lastName: '',
      secondLastName: '',
      id: '',
      course: '',
      birthDate: '',
      gender: 'M',
      qualitativeDescription: ''
    });
  };

  const openDeleteModal = (student: WithdrawnStudent) => {
    setStudentToDelete(student);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setStudentToDelete(null);
  };

  // Delete student permanently
  const deleteStudentPermanently = async () => {
    if (!studentToDelete) {
      return;
    }

    try {
      setLoading(true);
      const token = getToken();
      if (!token) {
        alert('No se encontró el token de autenticación');
        return;
      }

      const studentId = studentToDelete.originalId || studentToDelete.id;

      const response = await fetch(`${API_BASE_URL}/api/students/${studentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
        throw new Error(errorData.message || `Error HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setWithdrawnStudents(prev => prev.filter(student =>
          (student.originalId || student.id) !== studentId
        ));

        alert(`Estudiante "${studentToDelete.name}" eliminado permanentemente del sistema.`);
        closeDeleteModal();
      } else {
        throw new Error(data.message || 'Error al eliminar el estudiante');
      }
    } catch (error) {
      alert(`Error al eliminar el estudiante: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    } finally {
      setLoading(false);
    }
  };

  // Load available courses
  const loadAvailableCourses = async () => {
    try {
      setLoadingCourses(true);
      const token = getToken();
      if (!token) {
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/courses`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      if (data.success && Array.isArray(data.courses)) {
        setAvailableCourses(data.courses);
      } else {
        setAvailableCourses([]);
      }
    } catch (error) {
      setAvailableCourses([]);
    } finally {
      setLoadingCourses(false);
    }
  };

  // Parse student name into parts
  const parseStudentName = (name: string) => {
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length === 0) return { firstName: '', lastName: '', secondLastName: '' };
    if (parts.length === 1) return { firstName: parts[0], lastName: '', secondLastName: '' };
    if (parts.length === 2) return { firstName: parts[1], lastName: parts[0], secondLastName: '' };
    // Assume: LastName SecondLastName FirstName(s)
    return { 
      lastName: parts[0], 
      secondLastName: parts[1], 
      firstName: parts.slice(2).join(' ') 
    };
  };

  // Format date to YYYY-MM-DD for HTML date input
  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    
    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Try to parse DD/MM/YYYY format (4-digit year)
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
      const [day, month, year] = dateString.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    // Try to parse DD-MM-YYYY format (4-digit year)
    if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(dateString)) {
      const [day, month, year] = dateString.split('-');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    // Try to parse DD-MM-YY format (2-digit year) - Common format!
    if (/^\d{1,2}-\d{1,2}-\d{2}$/.test(dateString)) {
      const [day, month, shortYear] = dateString.split('-');
      // Convert 2-digit year to 4-digit (assume 2000s for years < 50, 1900s otherwise)
      const yearNum = parseInt(shortYear, 10);
      const fullYear = yearNum < 50 ? 2000 + yearNum : 1900 + yearNum;
      return `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    // Try to parse DD/MM/YY format (2-digit year)
    if (/^\d{1,2}\/\d{1,2}\/\d{2}$/.test(dateString)) {
      const [day, month, shortYear] = dateString.split('/');
      const yearNum = parseInt(shortYear, 10);
      const fullYear = yearNum < 50 ? 2000 + yearNum : 1900 + yearNum;
      return `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    // Try to parse ISO date string (with time)
    if (dateString.includes('T')) {
      return dateString.split('T')[0];
    }
    
    // Try JavaScript Date parsing as last resort
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    } catch (e) {
      console.error('Error parsing date:', dateString, e);
    }
    
    return dateString; // Return original if nothing works
  };

  // Open edit modal
  const openEditModal = (student: ActiveStudent) => {
    const nameParts = parseStudentName(student.name);
    const formattedBirthDate = formatDateForInput(student.birthDate);
    
    console.log('📅 Opening edit modal - Original birthDate:', student.birthDate, '-> Formatted:', formattedBirthDate);
    
    setEditStudentData({
      id: student.id,
      originalId: student.id,
      firstName: nameParts.firstName,
      lastName: nameParts.lastName,
      secondLastName: nameParts.secondLastName,
      newId: student.id,
      course: student.course,
      birthDate: formattedBirthDate,
      gender: (student as any).gender || 'M'
    });
    loadAvailableCourses(); // Load courses for dropdown
    setShowEditModal(true);
  };

  // Close edit modal
  const closeEditModal = () => {
    setShowEditModal(false);
    setEditStudentData({
      id: '',
      originalId: '',
      firstName: '',
      lastName: '',
      secondLastName: '',
      newId: '',
      course: '',
      birthDate: '',
      gender: 'M'
    });
  };

  // Save edited student
  const saveEditedStudent = async () => {
    if (!editStudentData.firstName || !editStudentData.lastName || !editStudentData.newId || 
        !editStudentData.course || !editStudentData.birthDate) {
      alert('Por favor completa todos los campos requeridos (*): Nombres, Apellido Paterno, ID/RUT, Curso y Fecha de Nacimiento.');
      return;
    }

    try {
      setEditLoading(true);
      const token = getToken();
      if (!token) {
        alert('❌ No se encontró token de autenticación');
        return;
      }

      const fullName = `${editStudentData.lastName}${editStudentData.secondLastName ? ' ' + editStudentData.secondLastName : ''} ${editStudentData.firstName}`.trim();
      
      const updateData = {
        name: fullName,
        firstName: editStudentData.firstName,
        lastName: editStudentData.lastName,
        secondLastName: editStudentData.secondLastName || '',
        newId: editStudentData.newId !== editStudentData.originalId ? editStudentData.newId : undefined,
        course: editStudentData.course,
        birthDate: editStudentData.birthDate,
        gender: editStudentData.gender
      };

      const response = await fetch(`${API_BASE_URL}/api/students/${editStudentData.originalId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Error HTTP ${response.status}`);
      }

      if (data.success) {
        alert(`✅ Estudiante "${fullName}" actualizado exitosamente`);
        closeEditModal();
        loadActiveStudents(); // Reload students
      } else {
        throw new Error(data.message || 'Error desconocido');
      }
    } catch (error) {
      alert(`❌ Error al actualizar estudiante: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    } finally {
      setEditLoading(false);
    }
  };

  // Toggle student selection
  const toggleStudentSelection = (studentId: string) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  // Select all visible students
  const selectAllStudents = () => {
    const filteredStudentIds = filteredActiveStudents.map(s => s.id);
    const allSelected = filteredStudentIds.every(id => selectedStudents.includes(id));
    
    if (allSelected) {
      setSelectedStudents(prev => prev.filter(id => !filteredStudentIds.includes(id)));
    } else {
      setSelectedStudents(prev => [...new Set([...prev, ...filteredStudentIds])]);
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedStudents([]);
  };

  // Open bulk course change modal
  const openBulkCourseModal = () => {
    if (selectedStudents.length === 0) {
      alert('Selecciona al menos un estudiante');
      return;
    }
    loadAvailableCourses(); // Load courses for dropdown
    setBulkNewCourse('');
    setShowBulkCourseModal(true);
  };

  // Execute bulk course change
  const executeBulkCourseChange = async () => {
    if (!bulkNewCourse) {
      alert('Selecciona un curso');
      return;
    }

    try {
      setEditLoading(true);
      const token = getToken();
      if (!token) {
        alert('❌ No se encontró token de autenticación');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/students/bulk/course`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          studentIds: selectedStudents,
          newCourse: bulkNewCourse
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Error HTTP ${response.status}`);
      }

      if (data.success) {
        const { results } = data;
        alert(`✅ ${results.success.length} estudiantes actualizados al curso "${bulkNewCourse}"\n${results.failed.length > 0 ? `⚠️ ${results.failed.length} fallidos` : ''}`);
        setShowBulkCourseModal(false);
        setBulkNewCourse('');
        setSelectedStudents([]);
        loadActiveStudents(); // Reload students
      } else {
        throw new Error(data.message || 'Error desconocido');
      }
    } catch (error) {
      alert(`❌ Error: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    } finally {
      setEditLoading(false);
    }
  };

  // Filter students for display
  const filteredActiveStudents = activeStudents.filter(student => {
    const matchesSearch = !searchTerm || 
      student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.id.toString().includes(searchTerm);
    const matchesCourse = !selectedCourse || student.course === selectedCourse;
    return matchesSearch && matchesCourse;
  });

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <FaUser className="text-2xl text-blue-600 mr-3" />
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Estudiantes</h1>
        </div>
        <button
          onClick={openAddStudentModal}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
          title="Agregar nuevo estudiante"
        >
          <FaUserPlus className="mr-2" />
          Agregar Estudiante
        </button>
      </div>

      {/* Header info */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-blue-800">
          <strong>📝 Esta sección es para agregar y editar estudiantes.</strong> Para retirar estudiantes o generar informes de notas masivos, ve a la sección <strong>"Informes y Retiros"</strong>.
        </p>
      </div>

      {/* Search and Filters */}
      <div className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="relative">
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder={`Buscar entre ${activeStudents.length} estudiantes por nombre o ID...`}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div>
            <select
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
            >
              <option value="">Todos los cursos ({activeStudents.length} estudiantes)</option>
              {courses.map(course => {
                const courseCount = activeStudents.filter(s => s.course === course).length;
                return (
                  <option key={course} value={course}>
                    {course} ({courseCount} estudiantes)
                  </option>
                );
              })}
            </select>
          </div>
        </div>
        
        {/* Search tips */}
        {activeStudents.length > 500 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-600">
            <p className="flex items-center">
              <FaSearch className="mr-2" />
              📊 Mostrando {activeStudents.length} estudiantes - usa los filtros para encontrar más rápido
            </p>
          </div>
        )}
      </div>

      {/* Loading */}
      {(loading || initialLoading) && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">{initialLoading ? 'Cargando estudiantes...' : 'Cargando...'}</p>
        </div>
      )}

      {/* Active Students List */}
      {!loading && !initialLoading && (
        <div className="space-y-4">
          {filteredActiveStudents.length === 0 ? (
            <div className="text-center py-8">
              <FaSearch className="mx-auto h-12 w-12 text-gray-400" />
              <p className="text-gray-500 mt-4">
                {searchTerm || selectedCourse ? 
                  'No se encontraron estudiantes con los filtros aplicados' : 
                  'No hay estudiantes activos disponibles'
                }
              </p>
              {(searchTerm || selectedCourse) && (
                <button
                  onClick={() => { setSearchTerm(''); setSelectedCourse(''); }}
                  className="mt-2 text-blue-600 hover:text-blue-800"
                >
                  Limpiar filtros
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Selection toolbar */}
              {selectedStudents.length > 0 && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <span className="text-indigo-800 font-medium">
                      <FaCheckSquare className="inline mr-2" />
                      {selectedStudents.length} estudiante{selectedStudents.length !== 1 ? 's' : ''} seleccionado{selectedStudents.length !== 1 ? 's' : ''}
                    </span>
                    <button
                      onClick={clearSelection}
                      className="text-indigo-600 hover:text-indigo-800 text-sm underline"
                    >
                      Limpiar selección
                    </button>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={openBulkCourseModal}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
                    >
                      <FaExchangeAlt className="mr-2" />
                      Cambiar Curso
                    </button>
                  </div>
                </div>
              )}

              {/* Results counter with select all */}
              <div className="mb-4 text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-lg flex items-center justify-between">
                <div>
                  📊 Mostrando <strong>{filteredActiveStudents.length} estudiantes</strong>
                  {searchTerm && (
                    <span> que coinciden con "{searchTerm}"</span>
                  )}
                  {selectedCourse && (
                    <span> del curso {selectedCourse}</span>
                  )}
                </div>
                <button
                  onClick={selectAllStudents}
                  className="text-indigo-600 hover:text-indigo-800 flex items-center text-sm font-medium"
                >
                  {filteredActiveStudents.every(s => selectedStudents.includes(s.id)) ? (
                    <>
                      <FaCheckSquare className="mr-1" />
                      Deseleccionar todos
                    </>
                  ) : (
                    <>
                      <FaSquare className="mr-1" />
                      Seleccionar todos
                    </>
                  )}
                </button>
              </div>
              
              {filteredActiveStudents.map((student) => {
                const progress = calculateProgress(student);
                const isSelected = selectedStudents.includes(student.id);
                return (
                  <div 
                    key={student.id} 
                    className={`border rounded-lg p-4 hover:shadow-md transition-all ${
                      isSelected ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start">
                      {/* Checkbox */}
                      <button
                        onClick={() => toggleStudentSelection(student.id)}
                        className={`mr-4 mt-1 p-1 rounded transition-colors ${
                          isSelected ? 'text-indigo-600' : 'text-gray-400 hover:text-gray-600'
                        }`}
                      >
                        {isSelected ? <FaCheckSquare className="text-xl" /> : <FaSquare className="text-xl" />}
                      </button>

                      <div className="flex-grow">
                        <div className="flex items-center mb-2">
                          <h3 className="text-lg font-semibold text-gray-900 mr-4">{student.name}</h3>
                          <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                            ID: {student.id}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                          <div>
                            <span className="font-medium">Curso:</span> {student.course}
                          </div>
                          <div>
                            <span className="font-medium">Fecha Nacimiento:</span> {student.birthDate}
                          </div>
                          <div>
                            <span className="font-medium">Progreso:</span> {progress.completed}/{progress.total} ({progress.percentage}%)
                          </div>
                        </div>
                        
                        {student.qualitativeDescription && (
                          <div className="mt-2 text-sm text-gray-600">
                            <span className="font-medium">Descripción:</span> {student.qualitativeDescription.substring(0, 100)}
                            {student.qualitativeDescription.length > 100 && '...'}
                          </div>
                        )}
                      </div>
                      
                      {/* Action buttons */}
                      <div className="flex flex-col space-y-2 ml-4">
                        <button
                          onClick={() => openEditModal(student)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                        >
                          <FaEdit className="mr-2" />
                          Editar
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* Add Student Modal */}
      {showAddStudentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <FaUser className="text-green-500 text-2xl mr-3" />
                <h2 className="text-xl font-bold text-gray-900">Agregar Nuevo Estudiante</h2>
              </div>
              <button
                onClick={closeAddStudentModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Nombres section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombres *
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    value={newStudentData.firstName}
                    onChange={(e) => setNewStudentData({ ...newStudentData, firstName: e.target.value })}
                    placeholder="Ej: María José"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Apellido Paterno *
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    value={newStudentData.lastName}
                    onChange={(e) => setNewStudentData({ ...newStudentData, lastName: e.target.value })}
                    placeholder="Ej: González"
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Apellido Materno
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    value={newStudentData.secondLastName}
                    onChange={(e) => setNewStudentData({ ...newStudentData, secondLastName: e.target.value })}
                    placeholder="Ej: López (opcional)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sexo *
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    value={newStudentData.gender}
                    onChange={(e) => setNewStudentData({ ...newStudentData, gender: e.target.value })}
                    required
                  >
                    <option value="M">Masculino (M)</option>
                    <option value="F">Femenino (F)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ID/RUT del estudiante *
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  value={newStudentData.id}
                  onChange={(e) => {
                    // Remove dashes automatically and convert to uppercase for K
                    const normalizedValue = e.target.value.replace(/-/g, '').toUpperCase();
                    setNewStudentData({ ...newStudentData, id: normalizedValue });
                  }}
                  placeholder="Ej: 12345678K o 123456789 (sin guión)"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  📝 Ingresa el RUT sin guión. Ejemplo: 12345678K o 123456789
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Curso *
                </label>
                {loadingCourses ? (
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 flex items-center">
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-green-500 mr-2"></div>
                    <span className="text-gray-500">Cargando cursos...</span>
                  </div>
                ) : availableCourses.length > 0 ? (
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    value={newStudentData.course}
                    onChange={(e) => setNewStudentData({ ...newStudentData, course: e.target.value })}
                    required
                  >
                    <option value="">Selecciona un curso</option>
                    {availableCourses.map((course, index) => (
                      <option key={index} value={course}>
                        {course}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="space-y-2">
                    <div className="w-full px-3 py-2 border border-yellow-300 rounded-md bg-yellow-50 text-yellow-800">
                      ⚠️ No se pudieron cargar los cursos. Usa el campo manual:
                    </div>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      value={newStudentData.course}
                      onChange={(e) => setNewStudentData({ ...newStudentData, course: e.target.value })}
                      placeholder="Ej: 4° Básico A"
                      required
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de nacimiento *
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  value={newStudentData.birthDate}
                  onChange={(e) => setNewStudentData({ ...newStudentData, birthDate: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción cualitativa (opcional)
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  rows={3}
                  value={newStudentData.qualitativeDescription}
                  onChange={(e) => setNewStudentData({ ...newStudentData, qualitativeDescription: e.target.value })}
                  placeholder="Observaciones adicionales sobre el estudiante..."
                />
              </div>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-md p-3 mt-4">
              <div className="flex">
                <FaUser className="text-green-400 mt-0.5 mr-2" />
                <div className="text-sm text-green-800">
                  <p className="font-medium">ℹ️ Información importante</p>
                  <p><strong>• Formato del nombre:</strong> Los apellidos y nombres se ordenarán automáticamente (Apellido Paterno + Apellido Materno + Nombres)</p>
                  <p><strong>• Curso:</strong> Selecciona de la lista para evitar errores de coincidencia</p>
                  <p><strong>• Habilidades:</strong> El estudiante será creado con habilidades vacías que podrán ser evaluadas posteriormente</p>
                </div>
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={closeAddStudentModal}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={addStudent}
                disabled={!newStudentData.firstName || !newStudentData.lastName || !newStudentData.id || !newStudentData.course || !newStudentData.birthDate || loading}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Agregando...' : 'Agregar Estudiante'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Student Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <FaEdit className="text-blue-500 text-2xl mr-3" />
                <h2 className="text-xl font-bold text-gray-900">Editar Estudiante</h2>
              </div>
              <button
                onClick={closeEditModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Nombres section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombres *
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={editStudentData.firstName}
                    onChange={(e) => setEditStudentData({ ...editStudentData, firstName: e.target.value })}
                    placeholder="Ej: María José"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Apellido Paterno *
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={editStudentData.lastName}
                    onChange={(e) => setEditStudentData({ ...editStudentData, lastName: e.target.value })}
                    placeholder="Ej: González"
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Apellido Materno
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={editStudentData.secondLastName}
                    onChange={(e) => setEditStudentData({ ...editStudentData, secondLastName: e.target.value })}
                    placeholder="Ej: López (opcional)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sexo *
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={editStudentData.gender}
                    onChange={(e) => setEditStudentData({ ...editStudentData, gender: e.target.value })}
                    required
                  >
                    <option value="M">Masculino (M)</option>
                    <option value="F">Femenino (F)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ID/RUT del estudiante *
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={editStudentData.newId}
                  onChange={(e) => {
                    // Remove dashes automatically and convert to uppercase for K
                    const normalizedValue = e.target.value.replace(/-/g, '').toUpperCase();
                    setEditStudentData({ ...editStudentData, newId: normalizedValue });
                  }}
                  placeholder="Ej: 12345678K o 123456789 (sin guión)"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  📝 Ingresa el RUT sin guión. Ejemplo: 12345678K o 123456789
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Curso *
                </label>
                {loadingCourses ? (
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 flex items-center">
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                    <span className="text-gray-500">Cargando cursos...</span>
                  </div>
                ) : availableCourses.length > 0 ? (
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={editStudentData.course}
                    onChange={(e) => setEditStudentData({ ...editStudentData, course: e.target.value })}
                    required
                  >
                    <option value="">Selecciona un curso</option>
                    {availableCourses.map((course, index) => (
                      <option key={index} value={course}>
                        {course}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={editStudentData.course}
                    onChange={(e) => setEditStudentData({ ...editStudentData, course: e.target.value })}
                    placeholder="Ej: 4° Básico A"
                    required
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de nacimiento *
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={editStudentData.birthDate}
                  onChange={(e) => setEditStudentData({ ...editStudentData, birthDate: e.target.value })}
                  required
                />
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={closeEditModal}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={saveEditedStudent}
                disabled={!editStudentData.firstName || !editStudentData.lastName || !editStudentData.newId || !editStudentData.course || !editStudentData.birthDate || editLoading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {editLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <FaEdit className="mr-2" />
                    Guardar Cambios
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Course Change Modal */}
      {showBulkCourseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <FaExchangeAlt className="text-indigo-500 text-2xl mr-3" />
                <h2 className="text-xl font-bold text-gray-900">Cambiar Curso</h2>
              </div>
              <button
                onClick={() => setShowBulkCourseModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                Vas a cambiar el curso de <strong>{selectedStudents.length}</strong> estudiante{selectedStudents.length !== 1 ? 's' : ''}.
              </p>
              
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nuevo Curso *
              </label>
              {loadingCourses ? (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 flex items-center">
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-500 mr-2"></div>
                  <span className="text-gray-500">Cargando cursos...</span>
                </div>
              ) : availableCourses.length > 0 ? (
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={bulkNewCourse}
                  onChange={(e) => setBulkNewCourse(e.target.value)}
                  required
                >
                  <option value="">Selecciona el nuevo curso</option>
                  {availableCourses.map((course, index) => (
                    <option key={index} value={course}>
                      {course}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={bulkNewCourse}
                  onChange={(e) => setBulkNewCourse(e.target.value)}
                  placeholder="Ej: 4° Básico A"
                  required
                />
              )}
            </div>

            <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3 mb-4">
              <p className="text-indigo-800 text-sm">
                ℹ️ Los estudiantes seleccionados serán movidos al nuevo curso. Sus evaluaciones y progreso se mantendrán.
              </p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowBulkCourseModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={executeBulkCourseChange}
                disabled={!bulkNewCourse || editLoading}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {editLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Cambiando...
                  </>
                ) : (
                  <>
                    <FaExchangeAlt className="mr-2" />
                    Cambiar Curso
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && studentToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              <FaTimes className="text-red-500 text-2xl mr-3" />
              <h2 className="text-xl font-bold text-gray-900">Eliminar Estudiante Permanentemente</h2>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 mb-3">
                ¿Estás seguro de que quieres eliminar permanentemente a <strong>{studentToDelete.name}</strong> del sistema?
              </p>
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-red-800 text-sm">
                  ⚠️ <strong>Advertencia:</strong> Esta acción no se puede deshacer. Se eliminarán todos los datos del estudiante de la base de datos.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={closeDeleteModal}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={deleteStudentPermanently}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center"
              >
                <FaTimes className="mr-2" />
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Retiro;
